import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { Shield, ArrowRight, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { Helmet } from 'react-helmet-async';

export default function AdminLoginPage() {
  const { passwordLogin, signInWithGoogle, isAdmin } = useUser();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogleAdminLogin = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      // The UserContext/FirebaseContext will detect the admin email and navigate via the useEffect below
    } catch (err: any) {
      if (err.code !== 'auth/popup-closed-by-user') {
        setError(err.message || 'Authorization failed');
      }
      setLoading(false);
    }
  };

  const handleAdminKeyLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const success = await passwordLogin(password);
      if (success) {
        navigate('/admin');
      } else {
        setError('Invalid Syndicate Access Key');
        setLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Authorization failed');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      navigate('/admin');
    }
  }, [isAdmin, navigate]);

  return (
    <div className="min-h-screen bg-ows-dark flex items-center justify-center p-4">
      <Helmet>
        <title>Admin Command | OWS</title>
      </Helmet>

      <div className="w-full max-w-md">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-white/10"
        >
          <div className="bg-ows-dark p-12 text-center border-b border-white/5">
            <div className="w-20 h-20 bg-ows-teal rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-ows-teal/20">
              <Shield className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-2xl font-display font-light uppercase tracking-[0.3em] text-white">Syndicate Command</h1>
            <p className="text-ows-sand/40 text-xs font-serif italic mt-2 uppercase tracking-widest leading-relaxed">Securitized Access Protocol</p>
          </div>

          <div className="p-12 bg-white">
            <div className="space-y-8">
              <div className="text-center">
                <p className="text-ows-dark/60 text-[10px] font-bold uppercase tracking-[0.2em] mb-6">Initialize Overwatch Protocol</p>
                <p className="text-[9px] text-ows-dark/30 uppercase tracking-widest leading-relaxed px-4">
                  Command access is strictly enforced via Google Cloud identity. Admin rights are bound to authorized Syndicate clearance.
                </p>
              </div>

              {error && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-[10px] text-red-500 font-bold uppercase tracking-widest text-center bg-red-50 py-3 rounded-xl border border-red-100"
                >
                  {error}
                </motion.p>
              )}
              
              <div className="space-y-4">
                <button 
                  onClick={handleGoogleAdminLogin}
                  disabled={loading}
                  className="w-full py-7 bg-ows-dark text-white rounded-[2rem] font-bold text-[10px] tracking-[0.4em] uppercase hover:bg-ows-teal transition-all flex items-center justify-center gap-4 shadow-2xl shadow-ows-teal/20 group font-mono"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="currentColor" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="currentColor" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="currentColor" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="currentColor" />
                  </svg>
                  {loading ? 'Verifying Identity...' : 'Dispatch Google Authorization'} 
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <p className="text-[8px] text-ows-dark/20 text-center uppercase tracking-widest px-8">
                  By authorizing, you confirm identity as OWS Coiled Command.
                </p>
              </div>

              <div className="text-center pt-8 border-t border-ows-teal/5">
                <button 
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-[9px] font-bold uppercase tracking-[0.2em] text-ows-dark/40 hover:text-ows-teal transition-colors flex items-center justify-center gap-2 mx-auto"
                >
                  <Lock className="h-3 w-3" /> Terminate Command & Exit
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="mt-8 text-center" id="admin-security-footer">
          <p className="text-[8px] text-white/20 uppercase tracking-[0.4em] font-bold">
            Authorized Personnel Only • IP Logged • OWS-CMD-2026
          </p>
        </div>
      </div>
    </div>
  );
}
